import React, { useEffect, useState } from 'react';
import { Button, TextField } from '~/UIKit';
import { useTranslation } from 'react-i18next';
import { useExport } from '~/Hooks';
import { makeStyles } from '~/UIKit/Style';
import { PrivateLayout } from '~/Layout/PrivateLayout';

const useStyles = makeStyles(() => ({
  input: {
    width: 400,
    height: 50,
  },
  exportPage: {
    marginBottom: 40,
    fontSize: 18,
  },
  textbox: {
    fontSize: 20,
    padding: '3% 33%',
    color: 'white',
    '& a': {
      color: 'white',
    },
  },
  h1: {
    fontSize: 32,
    margin: '30px 0',
  },
  h2: {
    fontSize: 28,
    fontWeight: 400,
    margin: '30px 0',
  },
  howToContinue: {
    fontSize: 22,
  },
  contentMargin: {},
  paragraph: {
    color: '#383E41',
    marginBottom: 20,
  },
  list: {
    color: '#383E41',
    marginBottom: 20,
    '& li': {
      marginBottom: 20,
    },
  },
  button: {
    backgroundColor: '#0084F8',
    borderRadius: 10,
    fontWeight: 400,
  },
  link: {
    textDecoration: 'underline',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  startExport: {
    marginTop: 50,
  },
  exportText: {
    '& p': {
      marginBottom: 0,
    },
  },
  startExportButton: {
    display: 'inline-block',
    width: 200,
    height: 55,
    marginLeft: 15,
  },
  exportStatus: {
    marginLeft: 10,
    fontWeight: 700,
    '& a': {
      color: 'inherit',
    },
  },
}));

export const ExportPage = () => {
  const { classes } = useStyles();
  const [state, setState] = useState({
    token: '',
    currentExportUuid: '',
    isButtonDisabled: true,
  });

  const { link, status, startExport, failureMessage } = useExport();

  const { t } = useTranslation();

  const handleChange = (e: any) =>
    setState({ ...state, token: e?.target?.value, isButtonDisabled: !e?.target?.value?.length });

  const handleSubmit = async () => {
    startExport(state);
    setState({ ...state, isButtonDisabled: true });
  };

  useEffect(() => {
    let target = link;
    if (target == null) {
      target = t('neo.export.eversportsDemoLink');
      return;
    }
    window.open(target, '_blank');
  }, [link]);

  useEffect(() => {
    if (!status) return;
    const divStatus = document.getElementById('export-status');
    let statusColor = 'color: rgb(255, 174, 0);';
    let statusText = 'Pending';
    switch (status) {
      case 'InProgress':
        statusText = 'InProgress';
        break;
      case 'Success':
        statusText = 'Success';
        statusColor = 'color: rgb(33, 226, 26);';
        setState({ ...state, isButtonDisabled: false });
        break;
      case 'Fail':
        statusText = failureMessage;
        statusColor = 'color: rgb(255, 66, 66);';
        setState({ ...state, isButtonDisabled: false });
        break;
    }
    if (divStatus) {
      divStatus.style.cssText = statusColor;
      divStatus.innerHTML = statusText;
    }
  }, [status]);

  return (
    <PrivateLayout hideBackButton>
      <div className={classes.exportPage}>
        <div className={classes.contentMargin}>
          <h2 className={classes.startExport}>{t('neo.export.intro.title')}</h2>
          <p className={classes.paragraph}>{t('neo.export.intro.description')}</p>
          <h2 className={classes.startExport}>{t('neo.export.startExportHeader')}</h2>
          <p className={classes.paragraph}>{t('neo.export.description')}</p>
          <TextField
            data-test-id="export-token"
            className={classes.input}
            variant="outlined"
            type="text"
            name="token"
            onChange={handleChange}
            placeholder={t('neo.export.authenticationToken')}
          />

          <Button
            className={classes.startExportButton}
            onClick={handleSubmit}
            disabled={state.isButtonDisabled}
            variant="contained"
            color="primary"
          >
            {t('neo.export.startExportButton')}
          </Button>
          <p>
            {!!link && (
              <a href={link} target="_blank" rel="noopener noreferrer">
                {t('neo.export.link.open')}
              </a>
            )}
          </p>
          <p id="export-status" className={classes.exportStatus} />
        </div>
      </div>
    </PrivateLayout>
  );
};
